export interface Oklch {
  l: number
  c: number
  h: number
}

const GAMUT_EPSILON = 1e-9
const CHROMA_SEARCH_CEILING = 0.5
const BISECTION_STEPS = 40

interface LinearRgb {
  r: number
  g: number
  b: number
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) {
    return min
  }
  if (value > max) {
    return max
  }
  return value
}

function srgbChannelToLinear(channel: number): number {
  const v = channel / 255
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
}

function linearChannelToSrgb(value: number): number {
  const v = value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055
  return v * 255
}

function normalizeHue(hue: number): number {
  const wrapped = hue % 360
  return wrapped < 0 ? wrapped + 360 : wrapped
}

function linearRgbToOklch(rgb: LinearRgb): Oklch {
  const l = Math.cbrt(0.4122214708 * rgb.r + 0.5363325363 * rgb.g + 0.0514459929 * rgb.b)
  const m = Math.cbrt(0.2119034982 * rgb.r + 0.6806995451 * rgb.g + 0.1073969566 * rgb.b)
  const s = Math.cbrt(0.0883024619 * rgb.r + 0.2817188376 * rgb.g + 0.6299787005 * rgb.b)
  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const b = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  return {
    l: lightness,
    c: Math.sqrt(a * a + b * b),
    h: normalizeHue((Math.atan2(b, a) * 180) / Math.PI),
  }
}

function oklchToLinearRgb(lightness: number, chroma: number, hue: number): LinearRgb {
  const radians = (normalizeHue(hue) * Math.PI) / 180
  const a = chroma * Math.cos(radians)
  const b = chroma * Math.sin(radians)
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3
  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  }
}

function isInGamut(lightness: number, chroma: number, hue: number): boolean {
  const rgb = oklchToLinearRgb(lightness, chroma, hue)
  const within = (v: number) => v >= -GAMUT_EPSILON && v <= 1 + GAMUT_EPSILON
  return within(rgb.r) && within(rgb.g) && within(rgb.b)
}

function largestInGamutChroma(lightness: number, ceiling: number, hue: number): number {
  if (isInGamut(lightness, ceiling, hue)) {
    return ceiling
  }
  let low = 0
  let high = ceiling
  for (let step = 0; step < BISECTION_STEPS; step += 1) {
    const mid = (low + high) / 2
    if (isInGamut(lightness, mid, hue)) {
      low = mid
    } else {
      high = mid
    }
  }
  return low
}

export function hexToOklch(hex: string): Oklch {
  const value = Number.parseInt(hex.slice(1), 16)
  return linearRgbToOklch({
    r: srgbChannelToLinear((value >> 16) & 0xff),
    g: srgbChannelToLinear((value >> 8) & 0xff),
    b: srgbChannelToLinear(value & 0xff),
  })
}

export function maxChromaFor(l: number, h: number): number {
  return largestInGamutChroma(clamp(l, 0, 1), CHROMA_SEARCH_CEILING, h)
}

export function oklchToHex(v: Oklch): string {
  const lightness = clamp(v.l, 0, 1)
  const hue = normalizeHue(v.h)
  const chroma = largestInGamutChroma(lightness, Math.max(v.c, 0), hue)
  const rgb = oklchToLinearRgb(lightness, chroma, hue)
  const format = (channel: number) =>
    clamp(Math.round(linearChannelToSrgb(channel)), 0, 255)
      .toString(16)
      .padStart(2, '0')
  return `#${format(rgb.r)}${format(rgb.g)}${format(rgb.b)}`
}
